import * as React from 'react';
import {
  ResponsiveLineCanvas,
  LineCanvasProps,
  LineSeries,
  PointTooltipProps,
} from '@nivo/line';
import classNames from 'classnames';
import toArray from 'lodash/toArray';
import difference from 'lodash/difference';
import deepmerge from 'deepmerge';

import { useTheme } from '../../../contexts/theme';
import { ColorStyleProps } from '../../../@types/theme';
import { parseChartColors } from '../../../utils/themeUtils';
import Legend from '../Legend';
import Spinner from '../../atoms/Spinner';
import ChartTooltip from '../../atoms/ChartTooltip';

/**
 * LineChart Tooltip
 */
const Tooltip: React.FC<PointTooltipProps<LineSeries>> = (p) => {
  const { point } = p;
  const datum = {
    id: point.id as string,
    color: point.color,
    label: `${point.seriesId} - ${point.data.xFormatted}`,
    value: point.data.y as number,
    hidden: false,
    formattedValue: point.data.yFormatted as string,
  };
  return <ChartTooltip datum={datum} useFormattedValue />;
};

// Interfaces
// //////////////////////////

export type LineChartOptions = Partial<LineCanvasProps<LineSeries>>;

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  data: Array<LineSeries>;
  options?: LineChartOptions;
  height?: number;
  averageOnLastSet?: boolean;
  allowDecimalScale?: boolean;
  displayLegend?: boolean;
}

/**
 * Default chart options
 */
const defaultOptions: LineChartOptions = {
  margin: {
    top: 20,
    right: 30,
    bottom: 60,
    left: 60,
  },
  yScale: {
    type: 'linear',
    min: 'auto',
    max: 'auto',
  },
  axisBottom: {
    ticksPosition: 'after',
    tickSize: 5,
    tickPadding: 5,
    tickRotation: 0,
    legendPosition: 'middle',
    legendOffset: 32,
  },
  axisLeft: {
    ticksPosition: 'before',
    tickSize: 5,
    tickPadding: 4,
    tickRotation: 0,
    legendOffset: -40,
    legendPosition: 'middle',
  },
  colors: { scheme: 'paired' },
  pointSize: 5,
  pointBorderWidth: 1,
  pointBorderColor: { theme: 'background' },
  enableArea: false,
  areaBaselineValue: 7.5,
  theme: {
    grid: {
      line: {
        strokeWidth: 0.5,
      },
    },
  },
};

/**
 * <LineChart> component
 */
export const LineChart: React.FunctionComponent<Props> = ({
  className = '',
  data,
  loading,
  options = {},
  height = 300,
  averageOnLastSet,
  allowDecimalScale = true,
  displayLegend = false,
  ...htmlElementProps
}: Props) => {
  const { theme } = useTheme();

  const colors = React.useMemo(
    () =>
      (options.colors
        ? toArray(options.colors)
        : theme.color.chart) as ColorStyleProps[],
    [options.colors, theme.color.chart],
  );

  // Prepare the legend ids
  const legend = React.useMemo(
    () =>
      data.map((d) => ({
        label: d.id,
        disabled: !d.data || d.data.length === 0,
      })),
    [data],
  );

  // Component states
  const [deselected, setDeselected] = React.useState<Array<string | number>>(
    [],
  );
  const [visibleColors, setVisibleColors] = React.useState([...colors]);

  const displayData = React.useMemo(() => {
    return data.filter((d) => !deselected.includes(d.id as string));
  }, [data, deselected]);

  const parsedColors = React.useMemo(
    () => parseChartColors(visibleColors),
    [visibleColors],
  );

  // Merge default, user and dynamic options
  const dynamicOptions: LineChartOptions = React.useMemo(() => {
    const format = (() => {
      if (options.axisLeft?.format) {
        return options.axisLeft?.format;
      }
      return !allowDecimalScale
        ? (e: number) => (Math.floor(e) === e ? e : '')
        : (e: number) => e;
    })();

    return {
      colors,
      theme: {
        fontSize: 12,
        fontFamily: theme.typography.fontFamily,
        textColor: theme.color.gray200,
        grid: {
          line: {
            stroke: theme.color.gray100,
          },
        },
      },
      axisLeft: {
        format,
        legend: options.axisLeft?.legend ? options.axisLeft.legend : '',
      },
      tooltip: Tooltip,
    };
  }, [
    allowDecimalScale,
    colors,
    options,
    theme.color.gray100,
    theme.color.gray200,
    theme.typography.fontFamily,
  ]);

  const opts: LineChartOptions = React.useMemo(() => {
    const defaultMerge = deepmerge(defaultOptions, options);

    // Dynamic options will overlay user options if needed
    const dynamicMerge = deepmerge(defaultMerge, dynamicOptions);

    // Set baseline
    // Ignore if user manually defined the baseline
    if (!options.yScale) {
      const pointsY: Array<number> = displayData
        .map((d) => d.data)
        .reduce((acc, val) => acc.concat(val), [])
        .filter((p) => p.y !== null)
        .map<number>((p) => p.y as number);
      const delta = Math.max(...pointsY) - Math.min(...pointsY);
      const minValue = Math.min(...pointsY) - delta / 5;

      return {
        ...dynamicMerge,
        areaBaselineValue: minValue,
        yScale: { type: 'linear', min: minValue },
      };
    }

    return dynamicMerge;
  }, [displayData, dynamicOptions, options]);

  // Toggles bars when a legend's badge is clicked
  const toggleData = React.useCallback(
    (key: string | number, index: string | number) => {
      const removedColors = difference(colors, visibleColors);
      const color = colors[index as number];

      const wasDeselected = deselected.includes(key as string);
      if (wasDeselected) {
        // Re-select label
        setDeselected((old) => old.filter((d) => d !== key));
        setVisibleColors(
          colors.filter((c, k) => !removedColors.includes(c) || index === k),
        );
      } else {
        // Deselect label
        setDeselected((old) => [...old, key]);
        setVisibleColors(visibleColors.filter((c) => c !== color));
      }
    },
    [colors, deselected, visibleColors],
  );

  // Find data length for styling
  const dataLength = data[0] && data[0].data ? data[0].data.length : 0;

  // Classes
  const wrapperCls = classNames(className, {
    'd-flex flex-justify-center flex-items-center': loading,
  });
  const chartCls = classNames(`line-chart dataset-${dataLength}`, {
    'avg-on-last-set': averageOnLastSet,
  });

  return (
    <div className={wrapperCls} {...htmlElementProps}>
      {loading && <Spinner data-testid="linechart-spinner" />}

      {!loading && (
        <>
          <div
            className={chartCls}
            style={{ height }}
            data-testid="chart-wrapper"
          >
            <ResponsiveLineCanvas
              data={displayData}
              {...opts}
              tooltip={Tooltip}
              colors={parsedColors}
            />
          </div>
          {displayLegend && legend && (
            <Legend keys={legend} colors={colors} onClick={toggleData} />
          )}
        </>
      )}
    </div>
  );
};

export default LineChart;
